import { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { PageHeader, StatusBadge, CustomerCombobox, QuickCustomerDialog, ConfirmDelete, usePaginated, Paginator } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Plus, Search, Download, Pencil, Trash2, Loader2, Gauge, ScanSearch, Wrench, Users, Droplet, ShieldCheck, Leaf } from "lucide-react";
import { toast } from "sonner";
import { CopyVin } from "@/components/CopyVin";

const empty = { spz: "", vin: "", brand: "", model: "", year: "", engine_ccm: "", power_kw: "", km: "", customer_id: "", note: "", tk_valid_until: "", ek_valid_until: "", next_service_date: "", next_service_km: "", last_oil_change_date: "" };
const normTxt = (s) => String(s || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().replace(/\s+/g, " ").trim();

const addMonths = (iso, months) => { const d = new Date(iso); d.setMonth(d.getMonth() + months); return d; };
const oilStatus = (v, months) => {
  if (!v.last_oil_change_date) return null;
  const due = addMonths(v.last_oil_change_date, months);
  const now = new Date(); now.setHours(0, 0, 0, 0);
  const days = Math.round((due - now) / 86400000);
  if (days < 0) return { overdue: true, days };
  if (days <= 30) return { overdue: false, days };
  return null;
};
const dateStatus = (iso) => {
  if (!iso) return null;
  const d = new Date(iso); d.setHours(0, 0, 0, 0);
  const now = new Date(); now.setHours(0, 0, 0, 0);
  const days = Math.round((d - now) / 86400000);
  if (days < 0) return { overdue: true, days };
  if (days <= 30) return { overdue: false, days };
  return null;
};

export default function Vehicles() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [lookupLoading, setLookupLoading] = useState(false);
  const [lookupCountry, setLookupCountry] = useState("SK");
  const [decodeLoading, setDecodeLoading] = useState(false);
  const [vinInfo, setVinInfo] = useState(null);
  const [saving, setSaving] = useState(false);
  const [detail, setDetail] = useState(null);
  const [newCustOpen, setNewCustOpen] = useState(false);
  const [newCustName, setNewCustName] = useState("");
  const [oilMonths, setOilMonths] = useState(12);
  const [ownerCompanies, setOwnerCompanies] = useState([]);
  useEffect(() => {
    if (!open || !form.customer_id) { setOwnerCompanies([]); return; }
    api.get(`/customers/${form.customer_id}/companies`).then((r) => setOwnerCompanies(r.data || [])).catch(() => setOwnerCompanies([]));
  }, [open, form.customer_id]);

  const load = useCallback(async () => {
    const { data } = await api.get("/vehicles");
    setItems(data);
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/customers").then((r) => setCustomers(r.data)); }, []);
  useEffect(() => { api.get("/settings").then((r) => setOilMonths(Number(r.data?.oil_change_interval_months) || 12)).catch(() => {}); }, []);
  const custName = (cid) => customers.find((c) => c.id === cid)?.name || "";
  const shown = items.filter((v) => {
    const s = normTxt(q);
    if (!s) return true;
    return [v.spz, v.vin, v.brand, v.model, custName(v.customer_id)].filter(Boolean).some((x) => normTxt(x).includes(s));
  });
  const pg = usePaginated(shown);

  const openNew = () => { setForm(empty); setEditId(null); setVinInfo(null); setOpen(true); };
  const openEdit = (v) => { setForm({ ...empty, ...v, year: v.year || "", engine_ccm: v.engine_ccm ?? "", power_kw: v.power_kw ?? "", km: v.km ?? "", customer_id: v.customer_id || "", tk_valid_until: v.tk_valid_until || "", ek_valid_until: v.ek_valid_until || "", next_service_date: v.next_service_date || "", next_service_km: v.next_service_km ?? "", last_oil_change_date: v.last_oil_change_date || "" }); setEditId(v.id); setVinInfo(null); setOpen(true); };
  const set = (k, val) => setForm((f) => ({ ...f, [k]: val }));

  const lookup = async () => {
    if (!form.spz) { toast.error("Zadajte ŠPZ"); return; }
    setLookupLoading(true);
    try {
      const { data } = await api.get("/lookup/vehicle", { params: { spz: form.spz, country: lookupCountry } });
      if (data.found) {
        setForm((f) => ({ ...f, vin: data.vin || f.vin, brand: data.brand || f.brand, model: data.model || f.model, year: data.year || f.year, engine_ccm: data.engine_ccm ?? f.engine_ccm, power_kw: data.power_kw ?? f.power_kw }));
        toast.success(data.cached ? "Doplnené z pamäte (bez poplatku)" : "VIN a údaje doplnené");
        if (data.credit_charged) toast.info(`−${data.credit_charged} ${data.credit_charged === 1 ? "kredit" : "kreditov"} · zostáva ${data.credits_left}`);
      } else {
        toast.warning(data.message || "Vozidlo sa nenašlo – doplňte ručne");
      }
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setLookupLoading(false); }
  };

  const decodeVin = async () => {
    const vin = (form.vin || "").trim();
    if (vin.length < 11) { toast.error("Zadajte platný VIN (min. 11 znakov)"); return; }
    setDecodeLoading(true);
    try {
      const { data } = await api.get("/lookup/vin", { params: { vin } });
      if (data.found) {
        setForm((f) => ({ ...f, brand: data.brand || f.brand, model: data.model || f.model, year: data.year || f.year, engine_ccm: data.engine_ccm ?? f.engine_ccm, power_kw: data.power_kw ?? f.power_kw }));
        setVinInfo(data);
        toast.success(data.cached ? "VIN dekódovaný (z pamäte)" : "VIN dekódovaný");
      } else {
        setVinInfo(null);
        toast.warning(data.message || "VIN sa nepodarilo dekódovať – doplňte ručne");
      }
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setDecodeLoading(false); }
  };

  const save = async () => {
    if (!form.spz.trim()) { toast.error("Zadajte ŠPZ"); return; }
    setSaving(true);
    const payload = { ...form, year: form.year ? Number(form.year) : null, engine_ccm: form.engine_ccm !== "" ? Number(form.engine_ccm) : null, power_kw: form.power_kw !== "" ? Number(form.power_kw) : null, km: form.km !== "" ? Number(form.km) : null, customer_id: form.customer_id || null, owner_company_id: ownerCompanies.some((c) => c.id === form.owner_company_id) ? form.owner_company_id : null, tk_valid_until: form.tk_valid_until || null, ek_valid_until: form.ek_valid_until || null, next_service_date: form.next_service_date || null, next_service_km: form.next_service_km !== "" ? Number(form.next_service_km) : null, last_oil_change_date: form.last_oil_change_date || null };
    try {
      if (editId) await api.put(`/vehicles/${editId}`, payload);
      else await api.post("/vehicles", payload);
      toast.success("Uložené"); setOpen(false); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const remove = async (id) => { await api.delete(`/vehicles/${id}`); toast.success("Zmazané"); load(); };
  const openDetail = async (id) => { const { data } = await api.get(`/vehicles/${id}`); setDetail(data); };
  useEffect(() => {
    const vid = searchParams.get("open");
    if (vid) { openDetail(vid).catch(() => {}); setSearchParams({}, { replace: true }); }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div>
      <PageHeader title="Vozidlá" subtitle="Evidencia vozidiel a KM"
        action={<Button className="min-h-[48px] gap-2" onClick={openNew} data-testid="new-vehicle-button"><Plus className="h-5 w-5" /> Pridať vozidlo</Button>} />

      <div className="relative mb-4 max-w-md">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input className="min-h-[48px] pl-9" placeholder="Hľadať ŠPZ, VIN, značku, meno majiteľa…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="vehicle-search" />
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {shown.length ? pg.paged.map((v) => (
          <div key={v.id} className="card-surface cursor-pointer p-4 transition-colors hover:border-primary/40" onClick={() => openDetail(v.id)} data-testid={`vehicle-card-${v.id}`}>
            <div className="flex items-center justify-between">
              <span className="plate" data-testid={`vehicle-spz-${v.id}`}>{v.spz}</span>
              {v.km != null && <span className="flex items-center gap-1 font-mono text-sm text-muted-foreground"><Gauge className="h-3.5 w-3.5" />{Number(v.km).toLocaleString("sk-SK")} km</span>}
            </div>
            <div className="mt-3 font-heading text-lg font-bold">{[v.brand, v.model].filter(Boolean).join(" ") || "—"}</div>
            <div className="text-sm text-muted-foreground">{v.year || ""} {v.vin && <>{v.year ? "· " : ""}<CopyVin vin={v.vin} testid={`vehicle-vin-copy-${v.id}`} /></>}</div>
            <div className="mt-1 flex items-center gap-1.5 text-sm font-medium text-foreground" data-testid={`vehicle-owner-${v.id}`}><Users className="h-3.5 w-3.5 text-muted-foreground" />{custName(v.customer_id) || "Bez majiteľa"}{v.owner_company_id && custName(v.owner_company_id) && <span className="text-xs text-muted-foreground" data-testid={`vehicle-company-${v.id}`}>· firma {custName(v.owner_company_id)}</span>}</div>
            <div className="mt-2 flex flex-wrap gap-1.5">
              {(() => { const s = dateStatus(v.tk_valid_until); return s ? (
                <div className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-semibold ${s.overdue ? "border-red-500/40 bg-red-500/10 text-red-400" : "border-amber-500/30 bg-amber-500/10 text-amber-400"}`} data-testid={`tk-badge-${v.id}`}>
                  <ShieldCheck className="h-3 w-3" /> TK {s.overdue ? `${Math.abs(s.days)} dní po termíne` : `o ${s.days} dní`}
                </div>) : null; })()}
              {(() => { const s = dateStatus(v.ek_valid_until); return s ? (
                <div className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-semibold ${s.overdue ? "border-red-500/40 bg-red-500/10 text-red-400" : "border-amber-500/30 bg-amber-500/10 text-amber-400"}`} data-testid={`ek-badge-${v.id}`}>
                  <Leaf className="h-3 w-3" /> EK {s.overdue ? `${Math.abs(s.days)} dní po termíne` : `o ${s.days} dní`}
                </div>) : null; })()}
              {(() => { const o = oilStatus(v, oilMonths); return o ? (
                <div className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-semibold ${o.overdue ? "border-red-500/40 bg-red-500/10 text-red-400" : "border-amber-500/30 bg-amber-500/10 text-amber-400"}`} data-testid={`oil-badge-${v.id}`}>
                  <Droplet className="h-3 w-3" /> Výmena oleja {o.overdue ? `${Math.abs(o.days)} dní po termíne` : `o ${o.days} dní`}
                </div>) : null; })()}
            </div>
            <div className="mt-3 flex flex-wrap gap-2" onClick={(e) => e.stopPropagation()}>
              <Button variant="secondary" size="sm" className="gap-1" onClick={() => navigate("/zakazky/nova", { state: { prefillVehicle: v } })} data-testid={`new-job-vehicle-${v.id}`}><Wrench className="h-3.5 w-3.5" /> Nová zákazka</Button>
              <Button variant="secondary" size="sm" className="gap-1" onClick={() => openEdit(v)} data-testid={`edit-vehicle-${v.id}`}><Pencil className="h-3.5 w-3.5" /> Upraviť</Button>
              <ConfirmDelete onConfirm={() => remove(v.id)} description={`Vozidlo ${v.spz} (${v.brand} ${v.model}) bude natrvalo odstránené.`}>
                <Button variant="ghost" size="sm" data-testid={`delete-vehicle-${v.id}`}><Trash2 className="h-3.5 w-3.5 text-destructive" /></Button>
              </ConfirmDelete>
            </div>
          </div>
        )) : null}
        {!shown.length && <div className="col-span-full py-12 text-center text-muted-foreground">Žiadne vozidlá.</div>}
      </div>

      <Paginator {...pg} />

      {/* Add/Edit dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
          <DialogHeader><DialogTitle className="font-heading text-2xl uppercase">{editId ? "Upraviť vozidlo" : "Nové vozidlo"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>Krajina ŠPZ (pre vyhľadanie VIN)</Label>
              <div className="mb-1 flex gap-1 rounded-lg border border-border bg-background/60 p-1">
                {["SK", "CZ"].map((cc) => (
                  <button key={cc} type="button" onClick={() => setLookupCountry(cc)} data-testid={`vehicle-country-${cc}`}
                    className={`flex-1 rounded-md px-3 py-1.5 text-sm font-semibold transition-colors ${lookupCountry === cc ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>{cc}</button>
                ))}
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>ŠPZ</Label>
              <div className="flex gap-2">
                <Input value={form.spz} onChange={(e) => set("spz", e.target.value.toUpperCase())} placeholder="BL123AB" data-testid="vehicle-spz-input" />
                <Button type="button" variant="outline" className="gap-1 whitespace-nowrap" onClick={lookup} disabled={lookupLoading} data-testid="vehicle-lookup-button">
                  {lookupLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} VIN
                </Button>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>VIN <span className="font-normal text-muted-foreground">(zadaj a dekóduj)</span></Label>
              <div className="flex gap-2">
                <Input value={form.vin} onChange={(e) => set("vin", e.target.value.toUpperCase())} className="font-mono" placeholder="napr. TMBEG7NE4J0123456" data-testid="vehicle-vin-input" />
                <Button type="button" variant="outline" className="gap-1 whitespace-nowrap" onClick={decodeVin} disabled={decodeLoading} data-testid="vehicle-decode-vin-button">
                  {decodeLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ScanSearch className="h-4 w-4" />} Dekódovať
                </Button>
              </div>
              {vinInfo && (
                <div className="mt-1 flex flex-wrap gap-1.5 text-xs" data-testid="vin-decoded-info">
                  {vinInfo.model_estimated && <span className="rounded bg-amber-500/15 px-2 py-0.5 text-amber-300">Model doplnený odhadom z VIN – skontrolujte</span>}
                  {vinInfo.fuel && <span className="rounded bg-secondary px-2 py-0.5">Palivo: {vinInfo.fuel}</span>}
                  {vinInfo.engine_l && <span className="rounded bg-secondary px-2 py-0.5">Objem: {vinInfo.engine_l} l</span>}
                  {vinInfo.power_hp && <span className="rounded bg-secondary px-2 py-0.5">Výkon: {vinInfo.power_hp} HP</span>}
                  {vinInfo.body && <span className="rounded bg-secondary px-2 py-0.5">Karoséria: {vinInfo.body}</span>}
                </div>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Značka</Label><Input value={form.brand} onChange={(e) => set("brand", e.target.value)} data-testid="vehicle-brand-input" /></div>
              <div className="space-y-1.5"><Label>Model</Label><Input value={form.model} onChange={(e) => set("model", e.target.value)} data-testid="vehicle-model-input" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Rok</Label><Input type="number" value={form.year} onChange={(e) => set("year", e.target.value)} data-testid="vehicle-year-input" /></div>
              <div className="space-y-1.5"><Label>Stav KM</Label><Input type="number" value={form.km} onChange={(e) => set("km", e.target.value)} data-testid="vehicle-km-input" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Objem motora (cm³)</Label><Input type="number" value={form.engine_ccm} onChange={(e) => set("engine_ccm", e.target.value)} placeholder="napr. 1968" data-testid="vehicle-engine-input" /></div>
              <div className="space-y-1.5"><Label>Výkon (kW)</Label><Input type="number" value={form.power_kw} onChange={(e) => set("power_kw", e.target.value)} placeholder="napr. 110" data-testid="vehicle-power-input" /></div>
            </div>
            <p className="-mt-2 text-xs text-muted-foreground">Objem a výkon pomôžu AI presnejšie odhadnúť normohodiny. Ak ich lookup nevráti, doplňte ich z technického preukazu.</p>
            <div className="space-y-1.5">
              <div className="flex items-center justify-between"><Label>Majiteľ (zákazník)</Label><button type="button" onClick={() => { setNewCustName(""); setNewCustOpen(true); }} className="text-xs font-semibold text-primary hover:underline" data-testid="vehicle-quick-add-customer">+ Nový zákazník</button></div>
              <CustomerCombobox customers={customers} value={form.customer_id} onChange={(id) => set("customer_id", id)}
                onAddNew={(name) => { setNewCustName(name); setNewCustOpen(true); }} placeholder="Bez majiteľa" testid="vehicle-customer" />
            </div>
            {ownerCompanies.length > 0 && (
              <div className="space-y-1.5">
                <Label>Firma (vlastník)</Label>
                <Select value={form.owner_company_id || "none"} onValueChange={(v) => set("owner_company_id", v === "none" ? null : v)}>
                  <SelectTrigger data-testid="vehicle-owner-company"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="none">Bez firmy (patrí osobe)</SelectItem>{ownerCompanies.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}{c.ico ? ` · IČO ${c.ico}` : ""}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Zrušiť</Button>
            <Button onClick={save} disabled={saving} data-testid="save-vehicle-button">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Uložiť"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail sheet */}
      <Sheet open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <SheetContent className="w-full overflow-y-auto border-border bg-card sm:max-w-md">
          {detail && (
            <>
              <SheetHeader><SheetTitle className="flex items-center gap-3"><span className="plate">{detail.spz}</span> {detail.brand} {detail.model}</SheetTitle></SheetHeader>
              <div className="mt-6 space-y-6">
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div><div className="text-xs uppercase text-muted-foreground">VIN</div><div className="font-mono">{detail.vin ? <CopyVin vin={detail.vin} testid="vehicle-detail-vin-copy" /> : "—"}</div></div>
                  <div><div className="text-xs uppercase text-muted-foreground">Rok</div><div>{detail.year || "—"}</div></div>
                  <div><div className="text-xs uppercase text-muted-foreground">Aktuálne KM</div><div className="font-mono">{detail.km != null ? Number(detail.km).toLocaleString("sk-SK") : "—"}</div></div>
                  <div><div className="text-xs uppercase text-muted-foreground">Platnosť TK</div><div>{detail.tk_valid_until ? new Date(detail.tk_valid_until).toLocaleDateString("sk-SK") : "—"}</div></div>
                  <div><div className="text-xs uppercase text-muted-foreground">Platnosť EK</div><div>{detail.ek_valid_until ? new Date(detail.ek_valid_until).toLocaleDateString("sk-SK") : "—"}</div></div>
                  <div><div className="text-xs uppercase text-muted-foreground">Ďalší servis</div><div>{detail.next_service_date ? new Date(detail.next_service_date).toLocaleDateString("sk-SK") : (detail.next_service_km ? `${Number(detail.next_service_km).toLocaleString("sk-SK")} km` : "—")}</div></div>
                  <div><div className="text-xs uppercase text-muted-foreground">Posledná výmena oleja</div><div>{detail.last_oil_change_date ? new Date(detail.last_oil_change_date).toLocaleDateString("sk-SK") : "—"}</div></div>
                </div>
                <div>
                  <h4 className="mb-2 font-heading text-sm font-bold uppercase text-muted-foreground">História KM</h4>
                  <div className="space-y-1">
                    {(detail.km_history || []).slice().reverse().map((h, i) => (
                      <div key={i} className="flex justify-between rounded border border-border bg-background/40 px-3 py-1.5 text-sm">
                        <span className="font-mono">{Number(h.km).toLocaleString("sk-SK")} km</span>
                        <span className="text-xs text-muted-foreground">{new Date(h.date).toLocaleDateString("sk-SK")} · {h.note}</span>
                      </div>
                    ))}
                    {!(detail.km_history || []).length && <p className="text-sm text-muted-foreground">Bez záznamov.</p>}
                  </div>
                </div>
                <div>
                  <h4 className="mb-2 font-heading text-sm font-bold uppercase text-muted-foreground">História zákaziek</h4>
                  <div className="space-y-2">
                    {(detail.jobs || []).map((j) => (
                      <div key={j.id} className="flex items-center justify-between rounded border border-border bg-background/40 px-3 py-2">
                        <div><div className="text-sm font-medium">{j.title}</div><div className="text-xs text-muted-foreground">{new Date(j.created_at).toLocaleDateString("sk-SK")}</div></div>
                        <StatusBadge status={j.status} />
                      </div>
                    ))}
                    {!(detail.jobs || []).length && <p className="text-sm text-muted-foreground">Žiadne zákazky.</p>}
                  </div>
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

      <QuickCustomerDialog open={newCustOpen} onOpenChange={setNewCustOpen} initialName={newCustName}
        onCreated={(c) => { setCustomers((prev) => [...prev, c]); set("customer_id", c.id); }}
        onPickExisting={(c) => set("customer_id", c.id)} />
    </div>
  );
}
